/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl, defineMessage} from 'react-intl'
import {
    Box,
    Flex,
    Stack,
    Text,
    useMultiStyleConfig,
    Button,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link, useLocation} from 'react-router-dom'
//Imports
import {UpgradeCartIcon} from '../../../components/icons'

//PARTIALS
import CartTitle from './cart-title'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'

const EmptyCart = ({removeMessages = []}) => {
    const style = useMultiStyleConfig('Cart', {removeMessagesLength: removeMessages.length})
    const [isHovered, setIsHovered] = useState(false)
    const {locale} = useMultiSite()
    const {formatMessage} = useIntl()
    const {customParams} = getConfig()
    const location = useLocation()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const cartTitleMessage = defineMessage({
        defaultMessage: 'Basket',
        id: 'cart_title.title.cart_title',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: formatMessage(cartTitleMessage),
        description: formatMessage(cartTitleMessage),
        url: currentPath,
    }

    return (
        <Box data-testid="sf-cart-empty" {...style?.emptyCart?.pageContainer} data-gtm="cart-page">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>

            <Flex {...style?.emptyCart?.headerContainer}>
                <CartTitle />
            </Flex>

            <Box {...style?.emptyCart?.cartContainer}>
                <Stack {...style?.emptyCart?.leftStack}>
                    <Box {...style?.emptyCart?.cartContent}>
                        <Button
                            variant={isHovered ? 'icon-button-primary' : 'icon-button-outline'}
                            {...style?.emptyCart?.discoverMoreButton}
                            as={Link}
                            to={`/${locale?.alias}`}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            aria-label={formatMessage({
                                id: 'empty_cart.button.discover.label',
                                defaultMessage: 'Continue shopping and discover more products',
                            })}
                        >
                            <UpgradeCartIcon
                                {...style?.emptyCart?.emptyIcon}
                                color={isHovered ? 'white' : 'black'}
                                aria-hidden="true"
                            />
                        </Button>

                        <Flex {...style?.emptyCart?.textContainer}>
                            <Text {...style?.emptyCart?.emptyCartTitle}>
                                <FormattedMessage
                                    defaultMessage="Your basket is empty."
                                    id="empty_cart.title"
                                />
                            </Text>
                            <Text {...style?.emptyCart?.emptyCartDescription}>
                                <FormattedMessage
                                    defaultMessage="Some supporting sentence here."
                                    id="empty_cart.description"
                                />
                            </Text>
                        </Flex>
                    </Box>
                </Stack>
            </Box>
            <Box id="cart_product_recommendations" overflowX="hidden"></Box>
        </Box>
    )
}
EmptyCart.propTypes = {
    isRegistered: PropTypes.bool,
    removeMessages: PropTypes.array,
}

export default EmptyCart
